// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

// Opt-out for the animated hazard critters beside a player who picked up hazards.
//
// Deliberately a LOCAL, per-device setting like the distance badge: it only affects what this
// device renders, so there is nothing to sync to a profile. Default is ON — the critters are the
// existing behaviour, so an unset flag has to keep them, and only an explicit 'off' hides them.

import { create } from 'zustand';

const STORAGE_KEY = 'goffy:hazardAnimations';

type AsyncStorageLike = {
  getItem: (key: string) => Promise<string | null>;
  setItem: (key: string, value: string) => Promise<void>;
};

let asyncStorage: AsyncStorageLike | null = null;
try {
  asyncStorage = require('@react-native-async-storage/async-storage').default as AsyncStorageLike;
} catch {
  asyncStorage = null;
}

const getLocalStorage = () => (typeof window !== 'undefined' ? window.localStorage : null);

let storageOverride: AsyncStorageLike | null = null;

const readStored = async (): Promise<string | null> => {
  if (storageOverride) return storageOverride.getItem(STORAGE_KEY);
  const local = getLocalStorage();
  if (local) return local.getItem(STORAGE_KEY);
  if (asyncStorage) return asyncStorage.getItem(STORAGE_KEY);
  return null;
};

const writeStored = async (value: string): Promise<void> => {
  if (storageOverride) return storageOverride.setItem(STORAGE_KEY, value);
  const local = getLocalStorage();
  if (local) {
    local.setItem(STORAGE_KEY, value);
    return;
  }
  if (asyncStorage) await asyncStorage.setItem(STORAGE_KEY, value);
};

type HazardAnimationState = {
  enabled: boolean;
  /** True once storage has been read, or once the flag was set explicitly. */
  hydrated: boolean;
  setEnabled: (enabled: boolean) => void;
};

export const useHazardAnimationState = create<HazardAnimationState>((set) => ({
  enabled: true,
  hydrated: false,
  setEnabled: (enabled) => {
    set({ enabled, hydrated: true });
    void writeStored(enabled ? 'on' : 'off').catch(() => {
      // Losing the mirror costs one re-toggle on the next cold start.
    });
  },
}));

export const hydrateHazardAnimations = async (): Promise<void> => {
  try {
    const stored = await readStored();
    if (stored === null) {
      useHazardAnimationState.setState({ hydrated: true });
      return;
    }
    if (useHazardAnimationState.getState().hydrated) return;
    // Anything but an explicit 'off' keeps them, so a corrupt value cannot silently hide them.
    useHazardAnimationState.setState({ enabled: stored !== 'off', hydrated: true });
  } catch {
    useHazardAnimationState.setState({ hydrated: true });
  }
};

// Hydrates on import; before that the flag reads as on, matching the default.
void hydrateHazardAnimations();

export const useHazardAnimationsEnabled = (): boolean => useHazardAnimationState((state) => state.enabled);

export const __setHazardAnimationStorageForTests = (storage: AsyncStorageLike | null) => {
  storageOverride = storage;
};

export const __resetHazardAnimationStateForTests = () => {
  useHazardAnimationState.setState({ enabled: true, hydrated: false });
};
